// pages/SearchResults.jsx  (or wherever your pages live)
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../../components/header/Header";
import Sidebar from "../../components/sidebar/Sidebar";
import { useMediaQuery } from "react-responsive";
import TutorCard from "../../components/TutorCard/TutorCard";
import api from "../../utils/api";
import "./SearchResults.css";
import debounce from "lodash/debounce";
import TutorCardSkeleton from "../../components/TutorCard/TutorCardSkeleton";
import BackToTop from "../../components/backToTop/BackToTop";
import { GooglePlacesInput } from "../../components/googlePlacesInput/GooglePlacesInput";
import Footer from "../../components/footer/Footer";
import { useUserLocation } from "../../providers/LocationProvider";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import RequestTutorModal from "../../components/requestTutor/RequestTutorModal";

const SearchResults = ({
  // Presets let the pretty SEO landing routes (/tutors/:location/:subject,
  // /tutors/:combinedSlug, /adult-education/:serviceSlug,
  // /training-centres/:location — see CategoryLandingPages.jsx) reuse this
  // same component/data-fetch instead of duplicating a search page. A
  // query-string value on the URL always wins over a preset.
  presetCategory = "",
  presetSubject = "",
  presetLocation = "",
  presetRole = "",
  canonicalPath = "",
  metaTitle = "",
  metaDescription = "",
} = {}) => {
  const { user } = useAuth();
  const [showRequestTutorModal, setShowRequestTutorModal] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const { setLocation } = useUserLocation();
  const { location } = useUserLocation();
  const initialRole = searchParams.get("role") || presetRole; // "tutor" or "institution"

  // Read initial values from URL
  const initialQuery = searchParams.get("q") || "";
  const initialSubject = searchParams.get("subject") || presetSubject || "all";
  const initialLocation = searchParams.get("location") || presetLocation;
  const initialCategory = searchParams.get("category") || presetCategory;   // ← ADD THIS

  const [locationFilter, setLocationFilter] = useState(initialLocation);
  const [selectedProgramTypes, setSelectedProgramTypes] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minRating, setMinRating] = useState("");

  // === NEW: Qualification / Degree filter states ===
  const [qualifications, setQualifications] = useState([]); // e.g. ["bsc", "msc", "phd", ...] pulled from DB
  const [selectedQualifications, setSelectedQualifications] = useState([]); // multi-select
  const [debouncedQualifications, setDebouncedQualifications] = useState([]);
const [selectedCategory, setSelectedCategory] = useState(initialCategory);
const [debouncedCategory, setDebouncedCategory] = useState(initialCategory);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedSubject, setSelectedSubject] = useState(initialSubject);
  const [radius, setRadius] = useState(25);
  const [selectedLat, setSelectedLat] = useState(null);
  const [selectedLng, setSelectedLng] = useState(null);

  const locationInputRef = useRef(null);

  const [itemsPerPage] = useState(12); // you can make controllable later
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(4);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [debouncedLocation, setDebouncedLocation] = useState(initialLocation);
  const [sortByDistance, setSortByDistance] = useState(false);

  const [providers, setProviders] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debouncedProgramTypes, setDebouncedProgramTypes] = useState([]);
  const [debouncedMinPrice, setDebouncedMinPrice] = useState("");
  const [debouncedMaxPrice, setDebouncedMaxPrice] = useState("");
  const [debouncedRating, setDebouncedRating] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [allProviders, setAllProviders] = useState([]); // merged results
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const observerRef = useRef(null);
  const isSmallMobile = useMediaQuery({ maxWidth: 820 });

  const isAuthenticated = !!user;

  const [programTypes, setProgramTypes] = useState([]);
  const safeTrim = (value) => (typeof value === "string" ? value.trim() : "");

  useEffect(() => {
    if (!isMobile) return;

    const container = document.querySelector(".tutors-grid");
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;

      if (
        scrollLeft >= maxScrollLeft - 100 && // near end
        !loading &&
        !isFetchingMore &&
        currentPage < pages
      ) {
        setIsFetchingMore(true);
        setCurrentPage((prev) => prev + 1);
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, [isMobile, loading, isFetchingMore, currentPage, pages]);

  useEffect(() => {
    const filters = document.querySelector(".filters-row");

    const handleScroll = () => {
      if (!filters) return;

      const offset = filters.getBoundingClientRect().top;

      if (offset <= 80) {
        filters.classList.add("stuck");
      } else {
        filters.classList.remove("stuck");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Scroll to top smoothly when the component mounts / route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth" if you prefer a gentle scroll
    });
  }, []);

  useEffect(() => {
    if (location && !selectedLat && !locationFilter) {
      setSelectedLat(location.lat);
      setSelectedLng(location.lng);
    }
  }, [location]);

  useEffect(() => {
    // Harmonized to exactly 3 modes everywhere (frontend, admin, backend).
    setProgramTypes(["online", "hybrid", "physical"]);
  }, []);

  useEffect(() => {
    if (initialRole === "tutor" || initialRole === "institution") {
      // console.log(`Pre-filtered by role: ${initialRole}`);
    }
  }, [initialRole]);

  // Fetch unique degrees/qualifications from DB (called once)
  useEffect(() => {
    const fetchQualifications = async () => {
      try {
        const response = await api.get("/users/qualifications");
        const uniqueDegrees =
          response.data?.qualifications || response.data || [];
        setQualifications(uniqueDegrees.filter(Boolean));
      } catch (err) {
        console.error("Failed to fetch qualifications for filter:", err);
        // Fallback to common Nigerian/academic degrees (you can expand)
        setQualifications([
          "bsc",
          "msc",
          "phd",
          "hnd",
          "ond",
          "diploma",
          "nce",
          "certificate",
        ]);
      }
    };

    fetchQualifications();
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true);
      // console.log("Loading:", loading);

      try {
        const params = new URLSearchParams();

        // Add role filter if it came from URL (or you can make it persistent state later)
        if (initialRole) {
          params.set("role", initialRole);
        }

        if (safeTrim(debouncedQuery)) params.set("q", safeTrim(debouncedQuery));
        if (selectedSubject !== "all") params.set("subject", selectedSubject);
        if (debouncedCategory)         params.set("category", debouncedCategory);  // ← ADD
        // if (debouncedLocation.trim())
        //   params.set("location", debouncedLocation.trim());

        // PRIORITY: GEO FILTER IF LAT/LNG EXISTS
        if (selectedLat && selectedLng) {
          params.set("lat", selectedLat);
          params.set("lng", selectedLng);
          params.set("radius", radius);
        } else if (safeTrim(debouncedLocation)) {
          params.set("location", safeTrim(debouncedLocation));
        }

        if (debouncedProgramTypes.length > 0)
          params.set("teachingMode", debouncedProgramTypes.join(","));

        if (debouncedMinPrice) params.set("minPrice", debouncedMinPrice);

        if (debouncedMaxPrice) params.set("maxPrice", debouncedMaxPrice);

        if (debouncedRating) params.set("minRating", debouncedRating);
        if (debouncedQualifications.length > 0) {
          params.set("degree", debouncedQualifications.join(","));
        }

        params.set("page", currentPage);
        params.set("limit", itemsPerPage);

        const response = await api.get(
          `/users/tutors-academies?${params.toString()}`,
        );

        const { data, total: totalCount, pages: totalPages } = response.data;

        // setProviders(data);
        console.log(response.data);
        let sortedData = [...data];

        if (sortByDistance && selectedLat && selectedLng) {
          sortedData.sort((a, b) => {
            const d1 = calculateDistance(
              selectedLat,
              selectedLng,
              a.location?.lat,
              a.location?.lng,
            );

            const d2 = calculateDistance(
              selectedLat,
              selectedLng,
              b.location?.lat,
              b.location?.lng,
            );

            return d1 - d2;
          });
        }
        if (isMobile) {
          setAllProviders((prev) => {
            // prevent duplicates
            const existingIds = new Set(prev.map((p) => p._id));
            const newItems = sortedData.filter((p) => !existingIds.has(p._id));

            return currentPage === 1 ? sortedData : [...prev, ...newItems];
          });
        } else {
          setProviders(sortedData);
          console.log("Providers API response:", providers);
        }
        // setProviders(sortedData);
        setTotal(totalCount || 0);
        setPages(totalPages || Math.ceil(totalCount / itemsPerPage));
        setLoading(false);
        setIsFetchingMore(false);
      } catch (err) {
        const message =
          err?.response?.data?.message || "Failed to load results";
        console.error(err);
        toast.error("No results found!");
        setLoading(false);
      }
    };

    fetchProviders();
  }, [
    debouncedQuery,
    debouncedLocation,
    selectedSubject,
    debouncedProgramTypes, // ✅ ADD
    debouncedMinPrice, // ✅ ADD
    debouncedMaxPrice, // ✅ ADD
    debouncedRating, // ✅ ADD
    debouncedQualifications,
    initialRole,
    currentPage,
    itemsPerPage,
    selectedLat, // ✅ ADD
    selectedLng, // ✅ ADD
    radius, // ✅ ADD
  ]);

  useEffect(() => {
    setCurrentPage(1);
    setAllProviders([]);
  }, [
    debouncedCategory,
    debouncedQuery,
    debouncedLocation,
    selectedSubject,
    debouncedProgramTypes,
    debouncedMinPrice,
    debouncedMaxPrice,
    debouncedRating,
    debouncedQualifications,
    selectedLat,
    selectedLng,
    radius,
  ]);

  const skipInitialUrlSync = useRef(Boolean(canonicalPath));

  useEffect(() => {
    // On the pretty SEO landing routes, the preset filters are already
    // implied by the path itself — skip echoing them into the query string
    // on mount so the clean URL stays clean until the visitor actually
    // changes a filter.
    if (skipInitialUrlSync.current) {
      skipInitialUrlSync.current = false;
      return;
    }

    const params = new URLSearchParams();

    if (safeTrim(searchQuery)) params.set("q", safeTrim(searchQuery));
    if (safeTrim(locationFilter))
      params.set("location", safeTrim(locationFilter));
    if (selectedSubject !== "all") {
      params.set("subject", selectedSubject);
    }
    if (debouncedCategory)  params.set("category", selectedCategory);  // ← 
    if (selectedProgramTypes.length > 0) {
      params.set("teachingMode", selectedProgramTypes.join(","));
    }
    if (minPrice !== "" && !isNaN(minPrice)) {
      params.set("minPrice", minPrice);
    }
    if (maxPrice !== "" && !isNaN(maxPrice)) {
      params.set("maxPrice", maxPrice);
    }
    if (selectedQualifications.length > 0) {
      params.set("degree", selectedQualifications.join(","));
    }

    setSearchParams(params, { replace: true });
  }, [
    selectedCategory,
    searchQuery,
    locationFilter,
    selectedSubject,
    selectedProgramTypes,
    selectedQualifications,
    minPrice,
    maxPrice,
    setSearchParams,
  ]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        setSelectedLat(lat);
        setSelectedLng(lng);

        setLocation({ lat, lng }); // 👈 GLOBAL SYNC
        setSortByDistance(true); // ✅ ENABLE SORTING

        // Optional: reverse geocode to readable address
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            const address = results[0].formatted_address;
            setLocationFilter(address);
            debouncedLocationUpdate(address);
          } else {
            setLocationFilter("My Location");
          }
        });
      },
      (error) => {
        console.error(error);
        alert("Unable to retrieve your location.");
      },
    );
  };

  // Helper to format mode labels nicely
  const formatModeLabel = (type) => {
    return type
      .replace(/[_-]/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // NEW: Pretty degree labels (BSc, MSc, PhD etc.)
  const formatDegreeLabel = (qual) => {
    if (!qual) return "";
    let label = qual.toUpperCase();
    label = label.replace("BSC", "BSc");
    label = label.replace("MSC", "MSc");
    label = label.replace("PHD", "PhD");
    label = label.replace("HND", "HND");
    label = label.replace("OND", "OND");
    return label;
  };

  // Toggle program type in array (used in checkbox onChange)
  const toggleProgramType = (type) => {
    setSelectedProgramTypes((prev) => {
      const updated = prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type];
      setCurrentPage(1);
      debouncedProgramTypeUpdate(updated);
      return updated;
    });
  };

  const toggleQualification = (qual) => {
    setSelectedQualifications((prev) => {
      const updated = prev.includes(qual)
        ? prev.filter((q) => q !== qual)
        : [...prev, qual];
      setCurrentPage(1);
      debouncedQualUpdate(updated);
      return updated;
    });
  };

  const totalItems = providers.length;

  // Backend already paginates → NEVER slice again
  const currentItems = isMobile ? allProviders : providers;

  // Use backend pages directly
  const totalPages = pages;

  // Debounce function — only updates debounced value after pause
  const debouncedSearchUpdate = useCallback(
    debounce((value) => {
      setDebouncedQuery(value);
    }, 500), // 500ms delay — adjust to taste (300–800ms common)
    [],
  );

  const debouncedCategoryUpdate = useCallback(
  debounce((value) => setDebouncedCategory(value), 500),
  [],
);

  const debouncedLocationUpdate = useCallback(
    debounce((value) => {
      setDebouncedLocation(value);
    }, 500),
    [],
  );

  const debouncedQualUpdate = useCallback(
    debounce((value) => setDebouncedQualifications(value), 500),
    [],
  );
  // When user types → update immediate state + trigger debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedSearchUpdate(value);
  };

  const debouncedProgramTypeUpdate = useCallback(
    debounce((value) => {
      setDebouncedProgramTypes(value);
    }, 500),
    [],
  );

  const debouncedMinPriceUpdate = useCallback(
    debounce((value) => {
      setDebouncedMinPrice(value);
    }, 500),
    [],
  );

  const debouncedMaxPriceUpdate = useCallback(
    debounce((value) => {
      setDebouncedMaxPrice(value);
    }, 500),
    [],
  );

  const debouncedRatingUpdate = useCallback(
    debounce((value) => {
      setDebouncedRating(value);
    }, 500),
    [],
  );

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocationFilter(value);
    setCurrentPage(1);
    debouncedLocationUpdate(value);
  };

  const handleMinPriceChange = (e) => {
    const value = e.target.value;
    setMinPrice(value);
    setCurrentPage(1);
    debouncedMinPriceUpdate(value);
  };
  const handleMaxPriceChange = (e) => {
    const value = e.target.value;
    setMaxPrice(value);
    setCurrentPage(1);
    debouncedMaxPriceUpdate(value);
  };

  const calculatePageNumber = (index, current, totalPages) => {
    if (totalPages <= 5) return index + 1;
    if (current <= 3) return index + 1;
    if (current >= totalPages - 2) return totalPages - 4 + index;
    return current - 2 + index;
  };

  const handleRatingChange = (value) => {
    setMinRating(value);
    setCurrentPage(1);
    debouncedRatingUpdate(value);
  };

  if (error) return <div className="error">{error}</div>;

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;

    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };
  // console.log("providers:", providers);
  // console.log("total:", total);
  // console.log("loading:", loading);

  const searchPageTitle = searchQuery
    ? `${searchQuery} Tutors & Training Centres | TutorsLinks`
    : initialRole === "tutor"
      ? "Find Verified Tutors in Nigeria | TutorsLinks"
      : initialRole === "institution"
        ? "Find Training Centres in Nigeria | TutorsLinks"
        : "Find Tutors & Training Centres in Nigeria | TutorsLinks";

  return (
    <div className="tutors-app">
      <Helmet>
        <title>{metaTitle || searchPageTitle}</title>
        <meta
          name="description"
          content={
            metaDescription ||
            "Search verified tutors and training centres in Nigeria by subject, location, price, and rating — for JAMB, WAEC, NECO, school subjects, skills, and adult education."
          }
        />
        {canonicalPath && (
          <link
            rel="canonical"
            href={`${window.location.origin}${canonicalPath}`}
          />
        )}
      </Helmet>

      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      {(!isAuthenticated || isSmallMobile) && (
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />
      )}

      <main
        className={`search-main ${
          sidebarOpen && (!isAuthenticated || isSmallMobile)
            ? "sidebar-open"
            : ""
        }`}
      >
        {" "}
        <div className="student-container">
          <div className="catalogue-controls">
            <form className="search-container" onSubmit={handleSearchChange}>
              <div className="search-bar">
                <img
                  src="/images/search.png"
                  alt="search"
                  className="search-logo"
                />

                <input
                  type="text"
                  placeholder="Search subject, tutor name, center, or location (e.g. Lagos)"
                  className="search-input full-width"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />

                <button type="submit" className="search-btn">
                  <img
                    src="/images/search-icon2.png"
                    alt="search"
                    className="search-icon2"
                  />
                  <span className="search-text">Search</span>
                </button>
              </div>
            </form>
            {/* Mobile Filter Toggle */}
            <button
              type="button"
              className="mobile-filter-toggle"
              onClick={() => setShowMobileFilters((prev) => !prev)}
            >
              <span>Filters</span>
              <span className={`arrow ${showMobileFilters ? "open" : ""}`}>
                ▼
              </span>
            </button>

            {/* Horizontal filters row */}
            <div className={`filters-row ${showMobileFilters ? "show" : ""}`}>
              <div className="filter-group location-group">
                <label htmlFor="location-input">Location</label>

                <div className="location-row">
                  <GooglePlacesInput
                    value={locationFilter}
                    placeholder="City, state or area"
                    onPlaceSelect={(place) => {
                      if (!place.geometry) return;

                      const lat = place.geometry.location.lat();
                      const lng = place.geometry.location.lng();

                      setSelectedLat(lat);
                      setSelectedLng(lng);
                      setLocationFilter(place.formatted_address);

                      setLocation({ lat, lng }); // ✅ GLOBAL

                      debouncedLocationUpdate(place.formatted_address);
                    }}
                    onChange={(value) => {
                      setLocationFilter(value);
                      setSelectedLat(null);
                      setSelectedLng(null);
                      setSortByDistance(false);
                      debouncedLocationUpdate(value);
                    }}
                    className="location-input"
                  />

                  {/* Radius Dropdown (ONLY when lat/lng exists) */}
                  {selectedLat && selectedLng && (
                    <select
                      value={radius}
                      onChange={(e) => setRadius(Number(e.target.value))}
                      className="radius-select"
                    >
                      <option value={5}>5 km</option>
                      <option value={10}>10 km</option>
                      <option value={25}>25 km</option>
                      <option value={50}>50 km</option>
                    </select>
                  )}

                  <button
                    type="button"
                    className="near-me-btn"
                    onClick={handleUseMyLocation}
                  >
                    📍 Use My Location
                  </button>
                </div>

                {/* Clean hint BELOW input */}
                {selectedLat && selectedLng && (
                  <p className="location-hint">
                    Showing results within <strong>{radius} km</strong>
                  </p>
                )}

                {sortByDistance && (
                  <p className="location-hint">
                    Showing nearest results first 📍
                  </p>
                )}
              </div>

              {/* Mode of Delivery - compact pills */}
              <div className="filter-group mode-group">
                <label>Mode of Delivery</label>
                <div className="mode-pills-container">
                  {programTypes.map((type) => (
                    <label
                      key={type}
                      className={`mode-pill ${
                        selectedProgramTypes.includes(type) ? "selected" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProgramTypes.includes(type)}
                        onChange={() => toggleProgramType(type)}
                        hidden
                      />
                      <span>{formatModeLabel(type)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range — disabled for now, not part of the current search filters */}
              {/* <div className="filter-group price-group">
                <label>Price (₦/hr)</label>
                <div className="price-range-wrapper">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minPrice}
                    onChange={handleMinPriceChange}
                    className="price-input"
                  />

                  <input
                    type="number"
                    placeholder="Max"
                    value={maxPrice}
                    onChange={handleMaxPriceChange}
                    className="price-input"
                  />
                </div>
              </div> */}

              {/* === Qualification / Degree Filter (Dropdown) — disabled for now === */}
              {/* <div className="filter-group qualification-group">
                <label htmlFor="qualification-select">Qualification</label>
                <select
                  id="qualification-select"
                  value={
                    selectedQualifications.length === 1
                      ? selectedQualifications[0]
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setSelectedQualifications([]);
                    } else {
                      setSelectedQualifications([value]); // single selection for simplicity
                    }
                    setCurrentPage(1);
                    debouncedQualUpdate(value ? [value] : []);
                  }}
                  className="qualification-select"
                >
                  <option value="">Any Qualification</option>
                  {qualifications.length > 0 ? (
                    qualifications.map((qual) => (
                      <option key={qual} value={qual}>
                        {formatDegreeLabel(qual)}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading...</option>
                  )}
                </select>
              </div> */}

              {/* Rating */}
              {/* <div className="filter-group rating-group">
                <label>Rating</label>
                <select
                  value={minRating}
                  onChange={(e) => handleRatingChange(e.target.value)}
                  className="rating-select"
                >
                  <option value="">Any Rating</option>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <option key={rating} value={rating}>
                      {rating}+
                    </option>
                  ))}
                </select>
              </div> */}
            </div>
          </div>
          <div className="page-title">
            <div className="search-title-row">
              <h2 className="search-main-title">
                {initialRole === "tutor"
                  ? "Featured Tutors"
                  : initialRole === "institution"
                    ? "Featured Training Centers"
                    : ""}
              </h2>
              <button
                type="button"
                className="request-tutor-trigger-btn"
                onClick={() => setShowRequestTutorModal(true)}
              >
                ✋ Can't find a match? Request a Tutor
              </button>
            </div>

            <p className="results-count">
              Showing {currentItems.length} of {total || 0}
              {initialRole === "tutor"
                ? " tutors"
                : initialRole === "institution"
                  ? " training centers"
                  : " tutors & centers"}
              {searchQuery && ` • "${searchQuery}"`}
              {selectedSubject !== "all" && ` • ${selectedSubject}`}
              {locationFilter && ` • near ${locationFilter}`}
              {selectedProgramTypes.length > 0 &&
                ` • ${selectedProgramTypes.map((t) => formatModeLabel(t)).join(", ")}`}
              {selectedQualifications.length > 0 &&
                ` • ${selectedQualifications.map(formatDegreeLabel).join(", ")}`}
              {minRating && ` • ${minRating}+ stars`}
            </p>
          </div>
          <div className="tutors-grid">
            {loading ? (
              isMobile ? (
                [...Array(2)].map((_, i) => (
                  <TutorCardSkeleton key={`skeleton-${i}`} />
                ))
              ) : (
                [...Array(itemsPerPage)].map((_, i) => (
                  <TutorCardSkeleton key={`skeleton-${i}`} />
                ))
              )
            ) : total === 0 ? (
              <div className="no-results">
                <p>No matches found.</p>
                <button
                  className="clear-filters-btn"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedSubject("all");
                    setLocationFilter("");
                    setSelectedProgramTypes([]);
                    setSelectedQualifications([]);
                    setMinPrice("");
                    setMaxPrice("");
                    setMinRating("");
                    setCurrentPage(1);
                    navigate("/search");
                  }}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              currentItems.map((provider) => (
                <TutorCard key={provider._id} tutor={provider} enableCompare />
              ))
            )}
          </div>

          {/* Pagination – desktop */}
          {!isMobile && pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>

              <div className="page-numbers">
                {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                  let pageNum = calculatePageNumber(i, currentPage, pages); // helper function below
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`page-num ${currentPage === pageNum ? "active" : ""}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {pages > 5 && currentPage < pages - 2 && (
                  <>
                    <span className="ellipsis">...</span>
                    <button
                      onClick={() => setCurrentPage(pages)}
                      className={`page-num ${currentPage === pages ? "active" : ""}`}
                    >
                      {pages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))}
                disabled={currentPage === pages}
                className="page-btn"
              >
                Next
              </button>
            </div>
          )}

          {isMobile && isFetchingMore && (
            <div className="horizontal-loader">
              {[...Array(2)].map((_, i) => (
                <TutorCardSkeleton key={`h-${i}`} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />

      {showRequestTutorModal && (
        <RequestTutorModal onClose={() => setShowRequestTutorModal(false)} />
      )}
    </div>
  );
};

export default SearchResults;
